/**
 * Element-level scroll-driven animations (multiple animated elements inside a scene).
 * Used when data.animations defines per-element keyframes/transforms + scrollTrigger.
 */

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { areAnimationsEnabled } from '../../../utils/animation-settings'
import type { SceneData, SceneConfig } from '../types'
import { createAnimationsContainer, createAnimationElement } from './animation-element'
import { calculateInitialTransformProps } from './animation-helpers'
import { normalizeTransformOrigin } from './utils'
import { createAnimationUpdateHandler, createThrottledHandlers } from './animation-update-handler'

export function setupElementAnimations(params: {
  imageContainer: HTMLDivElement
  data: SceneData
  config: SceneConfig
}): void {
  const { imageContainer, data, config } = params
  if (!data.animations?.length) return

  const animationsDisabled = !areAnimationsEnabled()
  let animationInstances: gsap.core.Tween[] = []

  const setup = () => {
    const img = imageContainer.querySelector('img')
    if (!img || img.complete === false) {
      setTimeout(setup, 100)
      return
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const firstClip = data.animations?.find((a) => a.clipPath)?.clipPath
        const animationsContainer = createAnimationsContainer(firstClip)

        data.animations!.forEach((animationConfig) => {
          const animElement = createAnimationElement({
            data,
            animationConfig,
          })
          animationsContainer.appendChild(animElement)
        })

        imageContainer.appendChild(animationsContainer)

        const viewportHeight = window.innerHeight
        const viewportCenter = viewportHeight / 2

        const createAllAnimations = () => {
          animationInstances.forEach((anim) => {
            const st = (anim as any).scrollTrigger
            if (st) st.kill()
            const cleanup = (anim as any).__cleanup
            if (cleanup) cleanup()
            anim.kill()
          })
          animationInstances = []

          data.animations!.forEach((animationConfig, index) => {
            const element = animationsContainer.children[index] as HTMLImageElement
            if (!element) return

            const parentContainer = element.parentElement!
            const parentRect = parentContainer.getBoundingClientRect()
            const parentHeight = parentRect.height
            const parentWidth = parentRect.width

            const { transformProps, initialTransformProps, keyframes } = calculateInitialTransformProps(
              animationConfig,
              parentHeight,
              parentWidth,
            )

            const scaleFromKeyframes = keyframes?.some((k: any) => k.scale !== undefined)
            const rotateFromKeyframes = keyframes?.some((k: any) => k.rotate !== undefined)
            if (animationConfig.transformOrigin) {
              const transformOrigin = normalizeTransformOrigin(animationConfig.transformOrigin)
              if (scaleFromKeyframes || (animationConfig.scale && typeof animationConfig.scale === 'object')) {
                gsap.set(element, { transformOrigin })
              }
              if (rotateFromKeyframes || animationConfig.rotate) {
                gsap.set(element, { transformOrigin })
              }
            }

            const translateYFromKeyframes = keyframes?.some((k: any) => k.translateY !== undefined)
            const translateXFromKeyframes = keyframes?.some((k: any) => k.translateX !== undefined)
            const hasKeyframeProps =
              keyframes &&
              (rotateFromKeyframes || scaleFromKeyframes || translateYFromKeyframes || translateXFromKeyframes)
            if (Object.keys(transformProps).length === 0 && !hasKeyframeProps) return

            gsap.set(element, initialTransformProps)
            void element.offsetHeight
            void element.getBoundingClientRect()

            if (animationsDisabled) return

            const startConfig = animationConfig.scrollTrigger.start
            const useFullViewportMode = startConfig === 'full'
            const useViewportMode = typeof startConfig === 'object' && startConfig !== null && !useFullViewportMode

            let pivot: 'center' | 'top' | 'bottom' = 'center'
            let startViewportPosition = 0
            let endViewportPosition = 0

            if (useFullViewportMode) {
              pivot = 'center'
              startViewportPosition = 0
              endViewportPosition = 0
            } else if (useViewportMode) {
              const elementPercent = startConfig.element
              const viewportPercent = startConfig.viewport
              const distancePercent = animationConfig.scrollTrigger.distance ?? 100
              const currentViewportHeight = window.innerHeight
              startViewportPosition = (viewportPercent / 100) * currentViewportHeight
              endViewportPosition = startViewportPosition - (distancePercent / 100) * currentViewportHeight
              if (elementPercent <= 10) pivot = 'top'
              else if (elementPercent >= 90) pivot = 'bottom'
              else pivot = 'center'
            } else {
              pivot =
                animationConfig.scrollTrigger.pivot ||
                (typeof startConfig === 'string' && startConfig === 'bottom'
                  ? 'bottom'
                  : typeof startConfig === 'string' && startConfig === 'top'
                    ? 'top'
                    : 'center')
              const scrollDistance = (animationConfig.scrollTrigger.scrollDistance ?? 1.0) * viewportHeight
              const halfDistance = scrollDistance / 2
              startViewportPosition = viewportCenter + halfDistance
              endViewportPosition = viewportCenter - halfDistance
            }

            const animationHandler = createAnimationUpdateHandler({
              element,
              elementRect: element.getBoundingClientRect(),
              animationConfig,
              parentContainer,
              parentHeight,
              parentWidth,
              transformProps,
              initialTransformProps,
              keyframes,
              scaleFromKeyframes,
              rotateFromKeyframes,
              pivot,
              startViewportPosition,
              endViewportPosition,
              useFullViewportMode,
              useViewportMode,
            })

            const { cleanup } = createThrottledHandlers(animationHandler.update, animationHandler.setNeedsRecalc)
            animationHandler.update()

            const animation = gsap.to({}, { duration: 0 })
            ;(animation as any).__cleanup = cleanup
            animationInstances.push(animation)
          })
        }

        const animImages = animationsContainer.querySelectorAll('img')
        let loadedCount = 0
        const totalImages = animImages.length

        const runCreateAndRefresh = () => {
          requestAnimationFrame(() => {
            createAllAnimations()
            requestAnimationFrame(() => ScrollTrigger.refresh())
          })
        }

        if (totalImages === 0) {
          runCreateAndRefresh()
        } else {
          animImages.forEach((img) => {
            if (img.complete) {
              loadedCount++
              if (loadedCount === totalImages) runCreateAndRefresh()
            } else {
              img.onload = () => {
                loadedCount++
                if (loadedCount === totalImages) runCreateAndRefresh()
              }
              img.onerror = () => {
                loadedCount++
                if (loadedCount === totalImages) runCreateAndRefresh()
              }
            }
          })
        }

        if (!animationsDisabled) {
          const resizeHandler = () => {
            createAllAnimations()
            ScrollTrigger.refresh()
          }
          window.addEventListener('resize', resizeHandler, { passive: true })
        }
      })
    })
  }

  requestAnimationFrame(() => {
    setTimeout(setup, 200)
  })
}
